import { Injectable, inject } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { Notification, NotificationResponse, GroupedNotification } from '@shared/common/interfaces';
import { NotificationApiService } from '@shared/common/api-services';
import { CurrentUserModel } from '@shared/common/models';

export class LoadNotifications {
    static readonly type = '[Notifications] Load Notifications';
    constructor(public refresh = false) {}
}

export class LoadMoreNotifications {
    static readonly type = '[Notifications] Load More Notifications';
}

export class MarkNotificationAsRead {
    static readonly type = '[Notifications] Mark As Read';
    constructor(public notificationId: string) {}
}

export class UpdateUnreadCount {
    static readonly type = '[Notifications] Update Unread Count';
}

export class AddRealTimeNotification {
    static readonly type = '[Notifications] Add Real Time Notification';
    constructor(public notification: Notification) {}
}

export class DeleteNotification {
    static readonly type = '[Notifications] Delete Notification';
    constructor(public notificationId: string) {}
}

export class MarkAllAsRead {
    static readonly type = '[Notifications] Mark All As Read';
}

export class ClearAllNotifications {
    static readonly type = '[Notifications] Clear All Notifications';
}

export class IncrementUnreadCount {
    static readonly type = '[Notifications] Increment Unread Count';
}

export interface NotificationsStateModel {
    notifications: Notification[];
    groupedNotifications: GroupedNotification[];
    unreadCount: number;
    nextToken?: string;
    loading: boolean;
    loadingMore: boolean;
    lastRealTimeUpdate?: number; // Timestamp of last real-time notification
}

@State<NotificationsStateModel>({
    name: 'notifications',
    defaults: {
        notifications: [],
        groupedNotifications: [],
        unreadCount: 0,
        loading: false,
        loadingMore: false,
        lastRealTimeUpdate: undefined,
    },
})
@Injectable()
export class NotificationsState {
    private currentUser = inject(CurrentUserModel);

    constructor(private notificationService: NotificationApiService) {}

    @Selector()
    static getNotifications(state: NotificationsStateModel): Notification[] {
        return state.notifications;
    }

    @Selector()
    static getGroupedNotifications(state: NotificationsStateModel): GroupedNotification[] {
        return state.groupedNotifications;
    }

    @Selector()
    static getUnreadCount(state: NotificationsStateModel): number {
        return state.unreadCount;
    }

    @Selector()
    static isLoading(state: NotificationsStateModel): boolean {
        return state.loading;
    }

    @Selector()
    static isLoadingMore(state: NotificationsStateModel): boolean {
        return state.loadingMore;
    }

    @Selector()
    static hasNextToken(state: NotificationsStateModel): boolean {
        return !!state.nextToken;
    }

    @Action(LoadNotifications)
    loadNotifications(ctx: StateContext<NotificationsStateModel>, action: LoadNotifications) {
        const state = ctx.getState();

        if (action.refresh) {
            ctx.patchState({ loading: true, notifications: [], nextToken: undefined });
        } else {
            ctx.patchState({ loading: true });
        }

        return this.notificationService.getNotifications().pipe(
            tap((response: NotificationResponse) => {
                const notifications = action.refresh
                    ? response.items
                    : [...state.notifications, ...response.items];
                const unreadCount = this.calculateLocalUnreadCount(notifications);
                ctx.patchState({
                    notifications,
                    groupedNotifications: this.groupNotificationsByDate(notifications),
                    nextToken: response.nextToken,
                    loading: false,
                    unreadCount,
                });
            }),
            catchError(() => {
                ctx.patchState({ loading: false });
                return of(null);
            }),
        );
    }

    @Action(LoadMoreNotifications)
    loadMoreNotifications(ctx: StateContext<NotificationsStateModel>) {
        const state = ctx.getState();

        if (!state.nextToken || state.loadingMore) {
            return of(null);
        }

        ctx.patchState({ loadingMore: true });

        return this.notificationService.getNotifications(state.nextToken).pipe(
            tap((response: NotificationResponse) => {
                const notifications = [...state.notifications, ...response.items];
                const unreadCount = this.calculateLocalUnreadCount(notifications);
                ctx.patchState({
                    notifications,
                    groupedNotifications: this.groupNotificationsByDate(notifications),
                    nextToken: response.nextToken,
                    loadingMore: false,
                    unreadCount,
                });
            }),
            catchError(() => {
                ctx.patchState({ loadingMore: false });
                return of(null);
            }),
        );
    }

    @Action(MarkNotificationAsRead)
    markAsRead(ctx: StateContext<NotificationsStateModel>, action: MarkNotificationAsRead) {
        const state = ctx.getState();
        const currentUserId = this.currentUser._id;
        const notifications = state.notifications.map((n) =>
            n._id === action.notificationId
                ? {
                      ...n,
                      readBy: n.readBy.includes(currentUserId)
                          ? n.readBy
                          : [...n.readBy, currentUserId],
                  }
                : n,
        );

        const unreadCount = this.calculateLocalUnreadCount(notifications);
        ctx.patchState({
            notifications,
            groupedNotifications: this.groupNotificationsByDate(notifications),
            unreadCount,
        });

        return this.notificationService.markAsRead(action.notificationId).pipe(
            tap(() => {
                ctx.dispatch(new UpdateUnreadCount());
            }),
            catchError((error) => {
                console.error('Failed to mark notification as read:', error);
                // Revert the optimistic update on error
                ctx.patchState({
                    notifications: state.notifications,
                    groupedNotifications: this.groupNotificationsByDate(state.notifications),
                });
                // Re-throw the error so it can be caught by the component
                throw error;
            }),
        );
    }

    @Action(UpdateUnreadCount)
    updateUnreadCount(ctx: StateContext<NotificationsStateModel>) {
        const currentState = ctx.getState();

        // If a real-time notification was received in the last 5 seconds, skip API call
        // to avoid overriding the fresh local count
        const fiveSecondsAgo = Date.now() - 5000;
        if (currentState.lastRealTimeUpdate && currentState.lastRealTimeUpdate > fiveSecondsAgo) {
            return of(null);
        }

        return this.notificationService.getUnreadCount().pipe(
            tap((response) => {
                ctx.patchState({ unreadCount: response.count });
            }),
            catchError(() => of(null)),
        );
    }

    @Action(AddRealTimeNotification)
    addRealTimeNotification(
        ctx: StateContext<NotificationsStateModel>,
        action: AddRealTimeNotification,
    ) {
        const state = ctx.getState();

        // Check if notification already exists to prevent duplicates
        const existingNotification = state.notifications.find(
            (n) => n._id === action.notification._id,
        );
        if (existingNotification) {
            return;
        }

        const notifications = [action.notification, ...state.notifications];
        const unreadCount = this.calculateLocalUnreadCount(notifications);

        ctx.patchState({
            notifications,
            groupedNotifications: this.groupNotificationsByDate(notifications),
            unreadCount,
            lastRealTimeUpdate: Date.now(),
        });
    }

    @Action(DeleteNotification)
    deleteNotification(ctx: StateContext<NotificationsStateModel>, action: DeleteNotification) {
        const state = ctx.getState();

        // Optimistically remove the notification from the state
        const notifications = state.notifications.filter((n) => n._id !== action.notificationId);
        const unreadCount = this.calculateLocalUnreadCount(notifications);

        ctx.patchState({
            notifications,
            groupedNotifications: this.groupNotificationsByDate(notifications),
            unreadCount,
        });

        return this.notificationService.deleteNotification(action.notificationId).pipe(
            tap(() => {
                // Update unread count after successful deletion
                ctx.dispatch(new UpdateUnreadCount());
            }),
            catchError((error) => {
                console.error('Failed to delete notification:', error);
                // Revert the optimistic update on error
                ctx.patchState({
                    notifications: state.notifications,
                    groupedNotifications: this.groupNotificationsByDate(state.notifications),
                });
                // Re-throw the error so it can be caught by the component
                throw error;
            }),
        );
    }

    @Action(MarkAllAsRead)
    markAllAsRead(ctx: StateContext<NotificationsStateModel>) {
        const state = ctx.getState();
        const currentUserId = this.currentUser._id;

        // Optimistically mark all notifications as read
        const notifications = state.notifications.map((n) => ({
            ...n,
            readBy: n.readBy.includes(currentUserId) ? n.readBy : [...n.readBy, currentUserId],
        }));

        const unreadCount = this.calculateLocalUnreadCount(notifications);
        ctx.patchState({
            notifications,
            groupedNotifications: this.groupNotificationsByDate(notifications),
            unreadCount,
        });

        // Make API call to mark all as read
        return this.notificationService.markAllAsRead().pipe(
            tap(() => {
                // Update unread count after successful operation
                ctx.dispatch(new UpdateUnreadCount());
            }),
            catchError((error) => {
                console.error('Failed to mark all notifications as read:', error);
                // Revert the optimistic update on error
                ctx.patchState({
                    notifications: state.notifications,
                    groupedNotifications: this.groupNotificationsByDate(state.notifications),
                    unreadCount: state.unreadCount,
                });
                throw error;
            }),
        );
    }

    @Action(ClearAllNotifications)
    clearAllNotifications(ctx: StateContext<NotificationsStateModel>) {
        const state = ctx.getState();

        // Optimistically clear all notifications
        ctx.patchState({
            notifications: [],
            groupedNotifications: [],
            unreadCount: 0,
            nextToken: undefined,
        });

        // Make API call to clear all notifications
        return this.notificationService.clearAllNotifications().pipe(
            tap(() => {
                // Update unread count after successful operation
                ctx.dispatch(new UpdateUnreadCount());
            }),
            catchError((error) => {
                console.error('Failed to clear all notifications:', error);
                // Revert the optimistic update on error
                ctx.patchState({
                    notifications: state.notifications,
                    groupedNotifications: this.groupNotificationsByDate(state.notifications),
                    unreadCount: state.unreadCount,
                    nextToken: state.nextToken,
                });
                throw error;
            }),
        );
    }

    private calculateLocalUnreadCount(notifications: Notification[]): number {
        const currentUserId = this.currentUser._id;
        return notifications.filter((notification) => !notification.readBy.includes(currentUserId))
            .length;
    }

    private groupNotificationsByDate(notifications: Notification[]): GroupedNotification[] {
        const groups = notifications.reduce(
            (acc, notification) => {
                const date = new Date(notification.createdAt).toDateString();
                if (!acc[date]) {
                    acc[date] = [];
                }
                acc[date].push(notification);
                return acc;
            },
            {} as Record<string, Notification[]>,
        );

        return Object.entries(groups)
            .map(([date, notifications]) => ({ date, notifications }))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }

    @Action(IncrementUnreadCount)
    incrementUnreadCount(ctx: StateContext<NotificationsStateModel>) {
        const state = ctx.getState();

        ctx.patchState({
            unreadCount: state.unreadCount + 1,
            lastRealTimeUpdate: Date.now(),
        });
    }
}
